import React, { useEffect, useState } from 'react';

import { translate } from '../../../services/translate';
import { crmService } from '../../../services/central-api/crm';
import { CRMParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Kanban from '../../../components/Kanban';
import { useQuery } from '@tanstack/react-query';
import alertAudioFile from '../../../../assets/alert.mp3';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import CrmCompaniesDropdown from '../../../components/crm/CrmCompaniesDropdown';

const sound = new Audio(alertAudioFile);

export default function KanbanMaintenanceRequests() {
    const { styles } = useStyles(styleSheet);
    const navigation = useNavigation<NavigationProp<CRMParamList, 'KanbanMaintenanceRequests'>>();
    const route = useRoute<RouteProp<CRMParamList, 'KanbanMaintenanceRequests'>>();
    const [companyId, setCompanyId] = useState<number>(route.params?.companyId ?? 0);
    const [playSound, setPlaySound] = useState<{ existingEvents: number[]; newEvents: number[]; }>({ existingEvents: [], newEvents: [] });
    const [selectedCompany, setSelectedCompany] = useState<{ id: number; name: string; }>();

    const isFocused = useIsFocused();
    const { data: maintenanceRequests, isInitialLoading } = useQuery({
        queryKey: ['getInstallationAndMaintenanceRequests', selectedCompany],
        queryFn: () => crmService.getKanbanMaintenanceRequests({ companyId: selectedCompany!.id }).then(async (result) => {

            if (maintenanceRequests) {
                const existingEvents = maintenanceRequests.filter(mr => !mr.finish && !mr.responsibleUser).map(ev => ev.id);
                const newEvents = result.filter(mr => !mr.finish && !mr.responsibleUser).map(ev => ev.id);

                setPlaySound({ existingEvents, newEvents });
            }

            return result;
        }),
        enabled: isFocused && Boolean(selectedCompany),
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (companyId) {
            navigation.setParams({
                companyId,
            });
        }
    }, [companyId]);


    function playAlertSound() {
        sound.currentTime = 0;
        sound.play().catch(err => console.error(err));
    }

    useEffect(() => {
        if (playSound.newEvents.length === 0) {
            return;
        }

        if (playSound.newEvents.some(item => !playSound.existingEvents.includes(item))) {
            console.log('sound alert');
            playAlertSound();
        }
    }, [playSound]);

    return (
        <Container>
            <Header
                title={translate('kanbanMaintenanceRequestsTitle')}
                actions={<CrmCompaniesDropdown
                    value={companyId}
                    setValue={setCompanyId}
                    zIndex={2}
                    viewStyle={{ minWidth: 225 }}
                    onSelect={(company) => {
                        setSelectedCompany(company);
                    }}
                />}
            />
            <View style={{ zIndex: -99, flex: 1 }}>
                {selectedCompany &&
                    <Kanban
                        isLoading={isInitialLoading}
                        columns={[{
                            key: 'pending',
                            label: translate('pending'),
                        }, {
                            key: 'under_analysis',
                            label: translate('under_analysis'),
                        }, {
                            key: 'in_progress',
                            label: translate('in_progress'),
                        }, {
                            key: 'review',
                            label: translate('review'),
                        }, {
                            key: 'concluded',
                            label: translate('concluded'),
                        }]}
                        cards={(maintenanceRequests || []).map(mr => {

                            let column = 'pending';

                            if (mr.finish) {
                                column = 'concluded';
                            } else if (mr.statusHistory.length) {
                                column = mr.statusHistory[0].status;
                            }

                            return {
                                column,
                                content: mr,
                                key: String(mr.id),
                            };
                        })}
                        cardContent={(item) => ({
                            id: item.content.id,
                            start: item.content.start,
                            finish: item.content.finish,
                            title: translate('maintenanceRequest'),
                            responsible: item.content.responsibleUser,
                            additionalInfo: [item.content.camera.serialNo ? item.content.camera.serialNo : item.content.camera.title],
                            priority: item.content.priority ? 'critical' : undefined,
                        })}
                        onClickCard={({ content: item }) => {
                            navigation.navigate('KanbanMaintenanceRequestDetail', { id: item.id });
                        }}
                    />
                }
                {!selectedCompany && <View style={styles.allSpaceCentered}>
                    <MyAppText style={styles.mainHeaderText}>{translate('requiredCompanySelection')}</MyAppText>
                </View>}
            </View>

        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    mainHeaderText: {
        fontSize: 22,
        color: theme.colors.neutral['500'],
        fontWeight: '700',
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
